import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Controller, useForm } from "react-hook-form";

import { zodResolver } from "@hookform/resolvers/zod";
import { Form, Input, Button, App } from "antd";

import { resetPassword } from "../api/resetPasswordAPI";
import { styles } from "../styles/resetPasswordStyles";
import { IResetPassword, ResetPasswordSchema } from "../type/resetPasswordType";

export default function ResetPasswordForm() {
  const navigate = useNavigate();
  const location = useLocation();
  const { message } = App.useApp();
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Extract token directly from URL query parameters
  const queryParams = new URLSearchParams(location.search);
  const token = queryParams.get("token");

  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<IResetPassword>({
    resolver: zodResolver(ResetPasswordSchema),
    defaultValues: {
      password: undefined,
    },
  });

  const onSubmit = async (data: IResetPassword) => {
    if (!token) {
      message.error("Reset token is missing");
      return;
    }

    setIsSubmitting(true);
    try {
      await resetPassword(token, data);
      message.success("Password reset successfully");
      navigate("/login");
    } catch (error: any) {
      message.error(error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Form
      name="resetPasswordForm"
      initialValues={{ rememberMe: false }}
      onFinish={handleSubmit(onSubmit)}
      labelAlign="left"
      colon={false}
      layout="vertical"
    >
      <Form.Item
        name="password"
        hasFeedback
        validateStatus={errors.password ? "error" : ""}
        help={errors.password?.message}
      >
        <Controller
          name="password"
          control={control}
          render={({ field }) => (
            <Input.Password placeholder="Password" {...field} size="large" />
          )}
        />
      </Form.Item>

      <Form.Item>
        <Button
          style={styles.resetPasswordButton}
          type="primary"
          htmlType="submit"
          size="large"
          loading={isSubmitting}
          disabled={!token}
        >
          Reset Password
        </Button>
      </Form.Item>
    </Form>
  );
}
